import * as React from "react"
import {useEffect, useState} from "react"
import {useNavigate} from "react-router-dom"
import {apiRequestDeactivateAccount, apiRequestUnsubscribe} from "../../api/apiRequests"
import {checkRedirect, setToken} from "../login/RequireAuth"
import Loader from "../ui/Loader"
import {useAppDispatch, useAppSelector} from "../../redux/storeHooks"
import {fetchFiles} from "../member/memberFilesSlice"
import {ROUTES} from "../../helpers/constants"

type AccountAction = "cancel" | "delete"
type ActionStatus = "idle" | "loading" | "error"

const confirmTexts: Record<AccountAction, {title: string, text: string, button: string}> = {
  cancel: {
    title: "Cancel your subscription?",
    text: "All future payments will stop. You keep access until the end of the period already paid.",
    button: "Cancel subscription",
  },
  delete: {
    title: "Delete your account?",
    text: "Your subscription will be cancelled and your account deactivated immediately. Your account data will be permanently deleted within 30 days.",
    button: "Delete my account",
  },
}

const AccountPage = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {filesStatus} = useAppSelector((store) => store.memberFilesState)
  const userEmail = useAppSelector((store) => store.loginState.userEmail)
  const [action, setAction] = useState<AccountAction | null>(null)
  const [status, setStatus] = useState<ActionStatus>("idle")
  const [accountClosed, setAccountClosed] = useState(false)

  useEffect(() => {
    dispatch(fetchFiles({navigate}))
  }, [dispatch, navigate])

  const closeModal = () => {
    setAction(null)
    setStatus("idle")
  }

  const confirmAction = async () => {
    setStatus("loading")
    const response = action === "cancel"
      ? await apiRequestUnsubscribe(userEmail)
      : await apiRequestDeactivateAccount()
    await checkRedirect({navigate}, response)
    if (response?.response !== "success") {
      setStatus("error")
      return
    }
    if (action === "cancel") {
      navigate(ROUTES.unsubscribeSuccess)
      return
    }
    setToken("")
    setAccountClosed(true)
    closeModal()
  }

  if (filesStatus === "loading") {
    return (
      <div className="min-h-500 relative">
        <Loader/>
      </div>
    )
  }

  const modal = action && (
    <div className="meeting-modal__overlay" onClick={closeModal}>
      <div className="meeting-modal__box" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="meeting-modal__x" onClick={closeModal} aria-label="Close">✕</button>
        <h3 className="meeting-modal__title">{confirmTexts[action].title}</h3>
        <p className="meeting-modal__subtitle">{confirmTexts[action].text}</p>
        {status === "error" && (
          <p className="meeting-modal__error">
            Something went wrong. Please try again or contact us at {document.pageInfo.supportEmail}.
          </p>
        )}
        <button type="button"
                className="meeting-modal__submit"
                disabled={status === "loading"}
                onClick={confirmAction}>
          {status === "loading" ? <Loader size="button"/> : confirmTexts[action].button}
        </button>
      </div>
    </div>
  )

  if (accountClosed) {
    return (
      <section className="section">
        <div className="login">
          <div className="auto icon--success margin-b-20"></div>
          <span className="title max-380 black bold auto block text-center margin-b-30">Your Account Has Been Deactivated</span>
          <p className="auto max-620 text-center margin-b-30 gray">
            Your account is now inactive and you will no longer have access to your account features. No further payments will be taken.
            Your account data will be permanently deleted within 30 days.
            We have sent a confirmation to your email.
          </p>
        </div>
      </section>
    )
  }

  return (
    <section className="section">
      <div className="login">
        <span className="title max-380 black bold auto block text-center margin-b-50">My Account</span>
        <div className="margin-b-50">
          <span className="block bold margin-b-20">Subscription</span>
          <p className="gray margin-b-20">Cancel anytime. All future payments stop, and you keep access until the end of the period already paid.</p>
          <button type="button"
                  className="ui-button ui-button--darkblue ui-button--themed width-100"
                  onClick={() => setAction("cancel")}>
            Cancel subscription
          </button>
        </div>
        <div>
          <span className="block bold margin-b-20">Account</span>
          <p className="gray margin-b-20">Your subscription will be cancelled and your account deactivated immediately. Your account data will be permanently deleted within 30 days.</p>
          <button type="button"
                  className="ui-button ui-button--darkblue ui-button--themed width-100"
                  onClick={() => setAction("delete")}>
            Delete my account
          </button>
        </div>
      </div>
      {modal}
    </section>
  )
}

export default AccountPage
